import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Drawer, Form, Select, Space, Typography, Checkbox, type CheckboxChangeEvent } from 'antd';
import debounce from 'lodash.debounce';
import { filterRangePresets } from 'shared/constants/filters.ts';
import type { ICargoFilter } from 'shared/stores/use-cargo-filters.ts';
import { useCargoFiltersStore } from 'shared/stores/use-cargo-filters.ts';
import { getCargoTypeOptions } from 'shared/utils/cargos/get-cargo-type-options';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { AddressField } from 'types/address.ts';
import { ClientFormSelect } from 'components/common/client-form-select';
import { DataRangeFormPicker } from 'components/common/data-range-form-picker';
import { UserFormSelect } from 'components/common/user-form-select';
import { CargoComplexStatusFilter } from '../cargo-complex-status-filter';
import { CargoFiltersAddressInput } from '../cargo-filters-address-input';
import { CargoFiltersCountryInput } from '../cargo-filters-country-input';
import { CargoFiltersDeliveryTruckInput } from '../cargo-filters-delivery-truck-input';
import { CargoFiltersStatusInput } from '../cargo-filters-status-input';
import styles from './cargos-filters-drawer.module.scss';
import type { ICargosFiltersDrawerProps } from './types';

export const CargosFiltersDrawer: React.FC<ICargosFiltersDrawerProps> = (props) => {
  const { form, onClose, isOpen } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useCargoFiltersStore();

  const handleSuccess = debounce(
    () => transformAndSetFilters<ICargoFilter>(form, setFilters, { preserveFalseKeys: true }),
    300,
  );
  const cargoTypeOptions = getCargoTypeOptions();

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  const cmrNotReceived = Form.useWatch('cmrNotReceived', form);
  const cargoStatuses = Form.useWatch('cargoStatuses', form);
  const complexStatusTypes = Form.useWatch('complexStatusTypes', form);

  const hasOldFilter = cargoStatuses?.length > 0;
  const hasComplexFilter = complexStatusTypes?.length > 0;

  const handleCmrChange = (e: CheckboxChangeEvent) => {
    if (!e.target.checked) {
      form.setFieldValue('deliveryTruckId', undefined);
    }
    handleSuccess();
  };

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='cargo-filter' layout='horizontal' initialValues={filters}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Typography.Text>Complex Status Filter</Typography.Text>
          <CargoComplexStatusFilter
            statusTypeName='complexStatusTypes'
            warehouseName='complexWarehouseFilters'
            handleSuccess={handleSuccess}
            className={styles.input}
            disabled={!!search || hasOldFilter}
          />
          <CargoFiltersStatusInput
            name='cargoStatuses'
            handleSuccess={handleSuccess}
            className={styles.input}
            disabled={!!search || hasComplexFilter}
          />
          <Form.Item name='types' className={styles.input}>
            <Select
              placeholder={t('cargo:cargo_type')}
              mode='multiple'
              options={cargoTypeOptions}
              disabled={!!search}
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>
          <Form.Item
            name='assetIsReturned'
            className={styles.input}
            getValueProps={(value) => ({ checked: value === false })}
            getValueFromEvent={(event: CheckboxChangeEvent) => (event.target.checked ? false : undefined)}>
            <Checkbox disabled={!!search} onChange={handleSuccess}>
              Container not returned
            </Checkbox>
          </Form.Item>
          <Typography.Text>{t('cargo:loading_info')}</Typography.Text>
          <DataRangeFormPicker
            name='loadingDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
          />
          <CargoFiltersCountryInput
            name='loadingPlaceCountryId'
            handleSuccess={handleSuccess}
            className={styles.input}
            disabled={!!search}
          />
          <CargoFiltersAddressInput
            name='loadingPlacePostCode'
            handleSuccess={handleSuccess}
            field={AddressField.postCode}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:post_code')}
          />
          <CargoFiltersAddressInput
            name='loadingPlaceName'
            handleSuccess={handleSuccess}
            field={AddressField.name}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:name')}
          />
          <CargoFiltersAddressInput
            name='loadingPlaceAddress'
            handleSuccess={handleSuccess}
            field={AddressField.address}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:address')}
          />
          <Typography.Text>{t('cargo:unloading_info')}</Typography.Text>
          <DataRangeFormPicker
            name='unloadingDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
          />
          <CargoFiltersCountryInput
            name='unloadingPlaceCountryId'
            handleSuccess={handleSuccess}
            className={styles.input}
            disabled={!!search}
          />
          <CargoFiltersAddressInput
            name='unloadingPlacePostCode'
            handleSuccess={handleSuccess}
            field={AddressField.postCode}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:post_code')}
          />
          <CargoFiltersAddressInput
            name='unloadingPlaceName'
            handleSuccess={handleSuccess}
            field={AddressField.name}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:name')}
          />
          <CargoFiltersAddressInput
            name='unloadingPlaceAddress'
            handleSuccess={handleSuccess}
            field={AddressField.address}
            className={styles.input}
            disabled={!!search}
            placeholder={t('common:address')}
          />
          <Typography.Text>Order</Typography.Text>
          <ClientFormSelect
            name='clientId'
            placeholder={t('client:client')}
            handleClientChange={handleSuccess}
            className={styles.input}
            allowClear
          />
          <UserFormSelect
            name='userId'
            placeholder={t('common:manager')}
            handleUserChange={handleSuccess}
            className={styles.input}
            disabled={!!search}
            allowClear
          />
          <DataRangeFormPicker
            name='orderDate'
            handleSuccess={handleSuccess}
            isRequired={false}
            className={styles.input}
            rangePresets={filterRangePresets}
          />
          <Typography.Text>CMR info</Typography.Text>
          <Form.Item name='cmrNotReceived' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleCmrChange}>CMR is not received</Checkbox>
          </Form.Item>
          {cmrNotReceived && (
            <CargoFiltersDeliveryTruckInput
              name='deliveryTruckId'
              handleSuccess={handleSuccess}
              disabled={!!search}
              className={styles.input}
            />
          )}
        </Space>
      </Form>
    </Drawer>
  );
};
